import { memo } from 'react';
import { Send, Mail, Bell, Users, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { BroadcastPreviewResult } from '../types';
import type { NotificationChannel } from '@masters-club/types';

const CHANNEL_META: Record<NotificationChannel, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  TELEGRAM: { label: 'Telegram', icon: Send },
  EMAIL: { label: 'Эл. почта', icon: Mail },
  APP: { label: 'В приложении', icon: Bell },
};

interface BroadcastSummaryProps {
  channels: NotificationChannel[];
  name: string;
  messageTemplate: string;
  scheduledAt: string;
  result: BroadcastPreviewResult | null;
  /** Точное число получателей из /count-recipients (вкладка «Получатели»). */
  recipientCount: number | null;
}

export const BroadcastSummary = memo(function BroadcastSummary({
  channels,
  name,
  messageTemplate,
  scheduledAt,
  result,
  recipientCount,
}: BroadcastSummaryProps) {
  // Приоритет — точный count из /count-recipients; fallback — число из preview.
  const effectiveCount = recipientCount ?? result?.recipientsCount ?? null;

  const warnings: string[] = [];
  if (!name.trim()) { warnings.push('Не указано название рассылки'); }
  if (!messageTemplate.trim()) { warnings.push('Пустой текст сообщения'); }
  if (channels.length === 0) { warnings.push('Не выбран ни один канал'); }
  if (effectiveCount === 0) { warnings.push('По текущим условиям нет получателей'); }

  const ready = warnings.length === 0;

  const scheduleLabel = scheduledAt
    ? new Date(scheduledAt).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Сразу после создания';

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center gap-2">
        {ready ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
        )}
        <h3 className="text-sm font-semibold">
          {ready ? 'Рассылка готова к отправке' : 'Перед отправкой проверьте'}
        </h3>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex items-start gap-2">
          <Users className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Получателей</p>
            <p className="text-sm font-semibold tabular-nums">
              {effectiveCount !== null ? effectiveCount.toLocaleString('ru-RU') : '—'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Send className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Каналы</p>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {channels.length === 0 ? (
                <span className="text-sm text-muted-foreground">—</span>
              ) : (
                channels.map((ch) => {
                  const meta = CHANNEL_META[ch];
                  const Icon = meta.icon;
                  return (
                    <Badge key={ch} variant="secondary" className="text-xs gap-1">
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </Badge>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Отправка</p>
            <p className="text-sm font-medium">{scheduleLabel}</p>
          </div>
        </div>
      </div>

      {warnings.length > 0 && (
        <ul className="space-y-1 border-t pt-3">
          {warnings.map((w) => (
            <li key={w} className="flex items-center gap-2 text-xs text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
